<template>
    <v-dialog v-model="visible" fullscreen scrollable transition="dialog-bottom-transition">
        <v-card class="security-card">
            <!-- Header -->
            <div class="card-header">
                <div class="left-section">
                    <div class="header-badge">{{ $t('security.header.badge') }}</div>
                    <h2>{{ $t('security.header.title') }}</h2>
                    <p class="header-description">
                        {{ $t('security.header.description') }}
                    </p>
                </div>
                <div class="right-section">
                    <button @click="closeDialog" class="dialog-close-btn">
                        <v-icon>mdi-close</v-icon>
                    </button>
                </div>
            </div>

            <!-- Main Content -->
            <div class="security-container">
                <!-- Security Flow Diagram -->
                <div class="flow-section">
                    <div class="section-header">
                        <h3>{{ $t('security.architecture.title') }}</h3>
                        <p>{{ $t('security.architecture.subtitle') }}</p>
                    </div>

                    <div class="security-flow">
                        <!-- User Layer -->
                        <div class="flow-node user-node">
                            <div class="node-icon">
                                <v-icon size="40" color="white">mdi-account-lock</v-icon>
                            </div>
                            <div class="node-content">
                                <h4>{{ $t('security.architecture.userLayer.title') }}</h4>
                                <p>{{ $t('security.architecture.userLayer.description') }}</p>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="flow-connector">
                            <div class="connector-line"></div>
                            <div class="connector-label">
                                <v-icon size="16">mdi-security</v-icon>
                                <span>{{ $t('security.architecture.connector1') }}</span>
                            </div>
                            <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                        </div>

                        <!-- Authentication Layer -->
                        <div class="flow-node auth-node">
                            <div class="node-icon">
                                <v-icon size="40" color="white">mdi-shield-account</v-icon>
                            </div>
                            <div class="node-content">
                                <h4>{{ $t('security.architecture.authLayer.title') }}</h4>
                                <p>{{ $t('security.architecture.authLayer.description') }}</p>
                            </div>
                            <div class="node-features">
                                <div class="feature-chip">OAuth 2.0</div>
                                <div class="feature-chip">SAML 2.0</div>
                                <div class="feature-chip">OpenID Connect</div>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="flow-connector">
                            <div class="connector-line"></div>
                            <div class="connector-label">
                                <v-icon size="16">mdi-shield-check</v-icon>
                                <span>{{ $t('security.architecture.connector2') }}</span>
                            </div>
                            <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                        </div>

                        <!-- Application Layer -->
                        <div class="flow-node app-node">
                            <div class="node-icon">
                                <v-icon size="40" color="white">mdi-application-brackets</v-icon>
                            </div>
                            <div class="node-content">
                                <h4>{{ $t('security.architecture.appLayer.title') }}</h4>
                                <p>{{ $t('security.architecture.appLayer.description') }}</p>
                            </div>
                            <div class="node-specs">
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('security.architecture.appLayer.feature1') }}</span>
                                </div>
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('security.architecture.appLayer.feature2') }}</span>
                                </div>
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('security.architecture.appLayer.feature3') }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- Arrow -->
                        <div class="flow-connector">
                            <div class="connector-line"></div>
                            <div class="connector-label">
                                <v-icon size="16">mdi-lock</v-icon>
                                <span>{{ $t('security.architecture.connector3') }}</span>
                            </div>
                            <v-icon class="connector-arrow" size="24">mdi-arrow-down-circle</v-icon>
                        </div>

                        <!-- Data Layer -->
                        <div class="flow-node data-node">
                            <div class="node-icon">
                                <v-icon size="40" color="white">mdi-database-lock</v-icon>
                            </div>
                            <div class="node-content">
                                <h4>{{ $t('security.architecture.dataLayer.title') }}</h4>
                                <p>{{ $t('security.architecture.dataLayer.description') }}</p>
                            </div>
                            <div class="node-specs">
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('security.architecture.dataLayer.feature1') }}</span>
                                </div>
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('security.architecture.dataLayer.feature2') }}</span>
                                </div>
                                <div class="spec-item">
                                    <v-icon size="16">mdi-check</v-icon>
                                    <span>{{ $t('security.architecture.dataLayer.feature3') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Security Features -->
                <div class="features-section">
                    <div class="section-header">
                        <h3>{{ $t('security.features.title') }}</h3>
                        <p>{{ $t('security.features.subtitle') }}</p>
                    </div>

                    <v-row class="features-grid">
                        <!-- Authentication Security -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="feature-card authentication">
                                <div class="feature-icon">
                                    <v-icon size="40" color="white">mdi-fingerprint</v-icon>
                                </div>
                                <h4>{{ $t('security.features.authentication.title') }}</h4>
                                <p>{{ $t('security.features.authentication.description') }}</p>
                                <div class="feature-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.authentication.feature1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.authentication.feature2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.authentication.feature3') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.authentication.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Data Protection -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="feature-card data-protection">
                                <div class="feature-icon">
                                    <v-icon size="40" color="white">mdi-shield-lock</v-icon>
                                </div>
                                <h4>{{ $t('security.features.dataProtection.title') }}</h4>
                                <p>{{ $t('security.features.dataProtection.description') }}</p>
                                <div class="feature-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.dataProtection.feature1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.dataProtection.feature2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.dataProtection.feature3') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.dataProtection.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Network Security -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="feature-card network-security">
                                <div class="feature-icon">
                                    <v-icon size="40" color="white">mdi-network-strength-4</v-icon>
                                </div>
                                <h4>{{ $t('security.features.networkSecurity.title') }}</h4>
                                <p>{{ $t('security.features.networkSecurity.description') }}</p>
                                <div class="feature-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.networkSecurity.feature1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.networkSecurity.feature2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.networkSecurity.feature3') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.networkSecurity.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Access Control -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="feature-card access-control">
                                <div class="feature-icon">
                                    <v-icon size="40" color="white">mdi-account-key</v-icon>
                                </div>
                                <h4>{{ $t('security.features.accessControl.title') }}</h4>
                                <p>{{ $t('security.features.accessControl.description') }}</p>
                                <div class="feature-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.accessControl.feature1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.accessControl.feature2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.accessControl.feature3') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.accessControl.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Monitoring & Auditing -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="feature-card monitoring">
                                <div class="feature-icon">
                                    <v-icon size="40" color="white">mdi-monitor-eye</v-icon>
                                </div>
                                <h4>{{ $t('security.features.monitoring.title') }}</h4>
                                <p>{{ $t('security.features.monitoring.description') }}</p>
                                <div class="feature-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.monitoring.feature1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.monitoring.feature2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.monitoring.feature3') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.monitoring.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- Incident Response -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="feature-card incident-response">
                                <div class="feature-icon">
                                    <v-icon size="40" color="white">mdi-shield-alert</v-icon>
                                </div>
                                <h4>{{ $t('security.features.incidentResponse.title') }}</h4>
                                <p>{{ $t('security.features.incidentResponse.description') }}</p>
                                <div class="feature-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.incidentResponse.feature1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.incidentResponse.feature2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.incidentResponse.feature3') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-circle</v-icon>
                                        <span>{{ $t('security.features.incidentResponse.feature4') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>

                <!-- Compliance Standards -->
                <div class="compliance-section">
                    <div class="section-header">
                        <h3>{{ $t('security.compliance.title') }}</h3>
                        <p>{{ $t('security.compliance.subtitle') }}</p>
                    </div>

                    <v-row class="compliance-grid">
                        <!-- ISO 27001 -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="compliance-card iso27001">
                                <div class="compliance-badge">
                                    <v-icon size="48" color="white">mdi-shield-check-outline</v-icon>
                                </div>
                                <h4>{{ $t('security.compliance.iso27001.title') }}</h4>
                                <h5>{{ $t('security.compliance.iso27001.subtitle') }}</h5>
                                <p>{{ $t('security.compliance.iso27001.description') }}</p>
                                <div class="compliance-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.iso27001.detail1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.iso27001.detail2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.iso27001.detail3') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- ISO 9001 -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="compliance-card iso9001">
                                <div class="compliance-badge">
                                    <v-icon size="48" color="white">mdi-certificate-outline</v-icon>
                                </div>
                                <h4>{{ $t('security.compliance.iso9001.title') }}</h4>
                                <h5>{{ $t('security.compliance.iso9001.subtitle') }}</h5>
                                <p>{{ $t('security.compliance.iso9001.description') }}</p>
                                <div class="compliance-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.iso9001.detail1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.iso9001.detail2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.iso9001.detail3') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- GDPR -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="compliance-card gdpr">
                                <div class="compliance-badge">
                                    <v-icon size="48" color="white">mdi-shield-key</v-icon>
                                </div>
                                <h4>{{ $t('security.compliance.gdpr.title') }}</h4>
                                <h5>{{ $t('security.compliance.gdpr.subtitle') }}</h5>
                                <p>{{ $t('security.compliance.gdpr.description') }}</p>
                                <div class="compliance-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.gdpr.detail1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.gdpr.detail2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.gdpr.detail3') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- SOC 2 -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="compliance-card soc2">
                                <div class="compliance-badge">
                                    <v-icon size="48" color="white">mdi-shield-check</v-icon>
                                </div>
                                <h4>{{ $t('security.compliance.soc2.title') }}</h4>
                                <h5>{{ $t('security.compliance.soc2.subtitle') }}</h5>
                                <p>{{ $t('security.compliance.soc2.description') }}</p>
                                <div class="compliance-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.soc2.detail1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.soc2.detail2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.soc2.detail3') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- NIS2 -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="compliance-card nis2">
                                <div class="compliance-badge">
                                    <v-icon size="48" color="white">mdi-security-network</v-icon>
                                </div>
                                <h4>{{ $t('security.compliance.nis2.title') }}</h4>
                                <h5>{{ $t('security.compliance.nis2.subtitle') }}</h5>
                                <p>{{ $t('security.compliance.nis2.description') }}</p>
                                <div class="compliance-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.nis2.detail1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.nis2.detail2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.nis2.detail3') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>

                        <!-- DSGVO -->
                        <v-col cols="12" md="6" lg="4" class="pa-3">
                            <div class="compliance-card dsgvo">
                                <div class="compliance-badge">
                                    <v-icon size="48" color="white">mdi-file-document-check</v-icon>
                                </div>
                                <h4>{{ $t('security.compliance.dsgvo.title') }}</h4>
                                <h5>{{ $t('security.compliance.dsgvo.subtitle') }}</h5>
                                <p>{{ $t('security.compliance.dsgvo.description') }}</p>
                                <div class="compliance-details">
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.dsgvo.detail1') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.dsgvo.detail2') }}</span>
                                    </div>
                                    <div class="detail-item">
                                        <v-icon size="16">mdi-check-bold</v-icon>
                                        <span>{{ $t('security.compliance.dsgvo.detail3') }}</span>
                                    </div>
                                </div>
                            </div>
                        </v-col>
                    </v-row>
                </div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const visible = ref(false);

function open() { 
    visible.value = true; 
}

function closeDialog() { 
    visible.value = false; 
}

defineExpose({ open, close: closeDialog });
</script>

<style scoped>
/* Card Base */
.security-card {
    background: linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%);
    min-height: 100vh;
    min-height: 100dvh;
    position: relative;
}

/* Header */
.card-header {
    background: linear-gradient(135deg, #0A1929 0%, #1A2F42 100%);
    padding: 32px 40px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.left-section {
    flex: 1;
}

.header-badge {
    display: inline-block;
    padding: 6px 16px;
    background: rgba(30, 90, 142, 0.2);
    border: 1px solid rgba(30, 90, 142, 0.3);
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 1px;
    color: #60a5fa;
    margin-bottom: 16px;
}

.card-header h2 {
    font-size: 32px;
    font-weight: 800;
    color: #ffffff;
    margin: 0 0 8px 0;
    letter-spacing: -0.5px;
}

.header-description {
    font-size: 15px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin: 0;
}

.right-section {
    margin-left: 24px;
}

.dialog-close-btn {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
}

.dialog-close-btn .v-icon {
    color: white;
}

/* Main Container */
.security-container {
    padding: 40px;
    max-width: 1600px;
    margin: 0 auto;
}

/* Section Styling */
.flow-section,
.features-section,
.compliance-section,
.practices-section {
    margin-bottom: 56px;
}

.section-header {
    text-align: center;
    margin-bottom: 40px;
}

.section-header h3 {
    font-size: 32px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 12px 0;
    letter-spacing: -0.5px;
}

.section-header p {
    font-size: 16px;
    color: #6b7280;
    margin: 0;
}

/* Security Flow */
.security-flow {
    background: white;
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
}

.flow-node {
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 20px;
    padding: 32px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    border: 2px solid transparent;
    width: 100%;
    max-width: 600px;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.flow-node:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.flow-node.user-node {
    border-color: rgba(139, 92, 246, 0.2);
}

.flow-node.user-node:hover {
    border-color: #8b5cf6;
}

.flow-node.auth-node {
    border-color: rgba(16, 185, 129, 0.2);
}

.flow-node.auth-node:hover {
    border-color: #10b981;
}

.flow-node.app-node {
    border-color: rgba(30, 90, 142, 0.2);
}

.flow-node.app-node:hover {
    border-color: #1E5A8E;
}

.flow-node.data-node {
    border-color: rgba(255, 107, 53, 0.2);
}

.flow-node.data-node:hover {
    border-color: #FF6B35;
}

.node-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.flow-node:hover .node-icon {
    transform: scale(1.1) rotate(-5deg);
}

.user-node .node-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.auth-node .node-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.app-node .node-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.data-node .node-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.node-content {
    text-align: center;
    margin-bottom: 16px;
}

.node-content h4 {
    font-size: 22px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 8px 0;
}

.node-content p {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
}

.node-features {
    display: flex;
    justify-content: center;
    gap: 8px;
    flex-wrap: wrap;
}

.feature-chip {
    padding: 6px 14px;
    background: #f3f4f6;
    color: #4b5563;
    font-size: 12px;
    font-weight: 600;
    border-radius: 20px;
}

.node-specs {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
}

.spec-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.spec-item .v-icon {
    color: #10b981;
}

.flow-connector {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 24px 0;
}

.connector-line {
    width: 2px;
    height: 60px;
    background: linear-gradient(180deg, #1E5A8E 0%, rgba(30, 90, 142, 0.3) 100%);
}

.connector-label {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: white;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #1E5A8E;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    white-space: nowrap;
}

.connector-arrow {
    color: #1E5A8E;
    margin-top: -8px;
    animation: bounce 2s ease-in-out infinite;
}

@keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(8px); }
}

/* Feature Cards */
.feature-card {
    background: white;
    border-radius: 20px;
    padding: 28px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.feature-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.feature-card.authentication:hover { border-color: #8b5cf6; }
.feature-card.encryption:hover { border-color: #1E5A8E; }
.feature-card.network:hover { border-color: #10b981; }
.feature-card.access:hover { border-color: #fbbf24; }
.feature-card.monitoring:hover { border-color: #FF6B35; }
.feature-card.backup:hover { border-color: #06b6d4; }

.feature-icon {
    width: 80px;
    height: 80px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.feature-card:hover .feature-icon {
    transform: scale(1.1) rotate(-5deg);
}

.feature-card.authentication .feature-icon {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.feature-card.encryption .feature-icon {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.feature-card.network .feature-icon {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.feature-card.access .feature-icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.feature-card.monitoring .feature-icon {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.feature-card.backup .feature-icon {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.feature-card h4 {
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 12px 0;
    text-align: center;
}

.feature-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
    text-align: center;
}

.feature-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.feature-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.feature-item .v-icon {
    color: #10b981;
}

/* Compliance Cards */
.compliance-card {
    background: white;
    border-radius: 20px;
    padding: 32px;
    height: 100%;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 2px solid transparent;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    text-align: center;
}

.compliance-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.compliance-card.iso27001:hover { border-color: #10b981; }
.compliance-card.iso9001:hover { border-color: #06b6d4; }
.compliance-card.gdpr:hover { border-color: #1E5A8E; }
.compliance-card.soc2:hover { border-color: #8b5cf6; }
.compliance-card.nis2:hover { border-color: #FF6B35; }
.compliance-card.dsgvo:hover { border-color: #fbbf24; }

.compliance-badge {
    width: 96px;
    height: 96px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    transition: transform 0.4s ease;
}

.compliance-card:hover .compliance-badge {
    transform: scale(1.1) rotate(5deg);
}

.compliance-card.iso27001 .compliance-badge {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.compliance-card.iso9001 .compliance-badge {
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
}

.compliance-card.gdpr .compliance-badge {
    background: linear-gradient(135deg, #1E5A8E 0%, #2B7BC4 100%);
}

.compliance-card.soc2 .compliance-badge {
    background: linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%);
}

.compliance-card.nis2 .compliance-badge {
    background: linear-gradient(135deg, #FF6B35 0%, #FFA07A 100%);
}

.compliance-card.dsgvo .compliance-badge {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.compliance-card h4 {
    font-size: 24px;
    font-weight: 800;
    color: #1f2937;
    margin: 0 0 4px 0;
}

.compliance-card h5 {
    font-size: 14px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.compliance-card > p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.7;
    margin: 0 0 20px 0;
}

.compliance-details {
    display: flex;
    flex-direction: column;
    gap: 10px;
    text-align: left;
}

.detail-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #4b5563;
    font-weight: 500;
}

.detail-item .v-icon {
    color: #10b981;
}

/* Responsive */
@media (max-width: 1024px) {
    .security-container {
        padding: 32px 24px;
    }

    .security-flow {
        padding: 32px 24px;
    }
}

@media (max-width: 768px) {
    .card-header {
        padding: 24px;
        flex-direction: column;
        gap: 20px;
    }

    .card-header h2 {
        font-size: 24px;
    }

    .header-description {
        font-size: 14px;
    }

    .right-section {
        margin-left: 0;
        align-self: flex-end;
    }

    .security-container {
        padding: 24px 16px;
    }

    .section-header h3 {
        font-size: 24px;
    }

    .security-flow {
        padding: 24px 16px;
    }

    .flow-node {
        padding: 24px;
    }

    .practices-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 480px) {
    .card-header h2 {
        font-size: 20px;
    }

    .security-container {
        padding: 20px 12px;
    }
}

/* Scrollbar */
.security-container::-webkit-scrollbar {
    width: 8px;
}

.security-container::-webkit-scrollbar-track {
    background: #f3f4f6;
}

.security-container::-webkit-scrollbar-thumb {
    background: #9ca3af;
    border-radius: 4px;
}

.security-container::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
}
</style>